import { forwardRef, type ReactNode, type CSSProperties, type HTMLAttributes } from 'react'

export type BadgeVariant = 'accent' | 'neutral' | 'green' | 'amber' | 'red'

/** A pill badge wrapping the shared `.badge badge-*` CSS classes (defined in
 *  global.css). The styling source of truth stays in CSS; this is the single
 *  React entry point so call sites don't hand-roll `<span className="badge ...">`.
 *  Ref + rest props (event handlers, aria-*, tabIndex) forward to the span so the
 *  badge can anchor a Tooltip - Tooltip cloneElements a ref and handlers onto its
 *  child, which silently no-op on components that swallow them. */
export const Badge = forwardRef<HTMLSpanElement, {
  variant?: BadgeVariant
  children: ReactNode
  style?: CSSProperties
  /** Extra class(es), e.g. `badge-still` to opt an amber badge out of the throb animation. */
  className?: string
} & HTMLAttributes<HTMLSpanElement>>(function Badge({ variant = 'neutral', children, style, className, ...rest }, ref) {
  return <span {...rest} ref={ref} className={`badge badge-${variant}${className ? ` ${className}` : ''}`} style={style}>{children}</span>
})

/** Map a vulnerability/severity tier to a badge variant. Single source of the
 *  critical→red, high/medium→amber, low/info→neutral colour mapping that every
 *  scan/security badge shares.
 *
 *  Takes `string`, not a closed union: severities arrive from scanners and the
 *  state/plan APIs, so an unrecognised tier is a data question, not a type error -
 *  it renders neutral rather than crashing or rendering unstyled. */
export function severityVariant(tier: string): BadgeVariant {
  switch (tier) {
    case 'critical': return 'red'
    case 'high':     return 'amber'
    case 'medium':   return 'amber'
    case 'low':      return 'neutral'
    case 'info':     return 'neutral'
    default:         return 'neutral'
  }
}

/** A severity rendered as its own label — `<SeverityBadge severity="high" />`. The two
 *  feature-local copies this replaced were byte-equivalent. */
export function SeverityBadge({ severity }: { severity: string }) {
  return <Badge variant={severityVariant(severity)}>{severity}</Badge>
}
